import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import { typographyStyles } from '../styles/typography';
import { createT } from '../utils/simpleTranslate';
import { formatDayHeaderForPDF, formatWeekRangeForPDF } from '../utils/dataFormatters';

export interface SurgeryPDFRow {
  patientName: string;
  healthId: string;
  dob: string;
  dateTime: string;
  indication: string;
  surgeryType: string;
  vpaStatus: string;
  surgeon: string;
  anesthetist: string;
}

export interface GroupedSurgeryData {
  date: Date;
  agendaName: string;
  rows: SurgeryPDFRow[];
}

interface SurgerySchedulePDFDocumentProps {
  weekData: GroupedSurgeryData[];
  weekRange: { start: Date; end: Date };
  config: PDFConfig;
}

// Column widths for landscape layout
const COL = {
  num:        '4%',
  patient:    '14%',
  healthId:   '7%',
  dob:        '7%',
  dateTime:   '8%',
  indication: '17%',
  surgeryType:'13%',
  vpa:        '6%',
  surgeon:    '12%',
  anesthetist:'12%',
};

const FONT_SIZE_HEADER = 8;
const FONT_SIZE_CELL = 7;

export const SurgerySchedulePDFDocument: React.FC<SurgerySchedulePDFDocumentProps> = ({
  weekData,
  weekRange,
  config,
}) => {
  const normalizedLocale = config.locale?.toLowerCase().startsWith('en') ? 'en' :
                           config.locale?.toLowerCase().startsWith('es') ? 'es' : 'fr';
  const t = createT(normalizedLocale);

  const totalPatients = weekData.reduce((sum, group) => sum + group.rows.length, 0);

  const documentTitle = React.useMemo(() => {
    const title = t('pages.hospital.surgery.schedule_pdf.title', 'Programme Chirurgical');
    const weekOf = t('pages.hospital.surgery.schedule_pdf.week_of', 'Semaine du');
    const range = formatWeekRangeForPDF(weekRange.start, weekRange.end, normalizedLocale);
    const patientWord = t('general.patient', 'patient');
    return `${title} — ${weekOf} ${range} (${totalPatients} ${patientWord}${totalPatients > 1 ? 's' : ''})`;
  }, [weekRange, totalPatients, normalizedLocale]);

  const tableStyles = {
    headerRow: {
      flexDirection: 'row' as const,
      borderBottomWidth: 1.5,
      borderBottomColor: '#000',
      paddingBottom: 3,
      paddingTop: 3,
      paddingHorizontal: 4,
      backgroundColor: '#f0f0f0',
    },
    headerCell: {
      ...typographyStyles.body2,
      fontWeight: 'bold',
      fontSize: FONT_SIZE_HEADER,
    },
    row: {
      flexDirection: 'row' as const,
      paddingVertical: 3,
      paddingHorizontal: 4,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E0E0E0',
      minHeight: 18,
    },
    cell: {
      ...typographyStyles.body2,
      fontSize: FONT_SIZE_CELL,
    },
    dayHeader: {
      ...typographyStyles.body2,
      fontSize: 10,
      fontWeight: 'bold',
      backgroundColor: '#d0d0d0',
      paddingVertical: 4,
      paddingHorizontal: 6,
      marginTop: 8,
    },
    agendaHeader: {
      ...typographyStyles.body2,
      fontSize: 9,
      fontWeight: 'bold',
      backgroundColor: '#e8e8e8',
      paddingVertical: 3,
      paddingHorizontal: 6,
    },
  };

  const renderTableHeader = () => (
    <View style={tableStyles.headerRow}>
      <Text style={[tableStyles.headerCell, { width: COL.num }]}>#</Text>
      <Text style={[tableStyles.headerCell, { width: COL.patient }]}>
        {t('general.patient', 'Patient')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.healthId }]}>
        {t('investigation.create.personal_data.fields.health_id', 'ID')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.dob }]}>
        {t('investigation.create.personal_data.fields.birthdate', 'DOB')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.dateTime }]}>
        {t('general.date', 'Date')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.indication }]}>
        {t('pages.hospital.surgery.schedule_pdf.indication', 'Indication')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.surgeryType }]}>
        {t('pages.hospital.surgery.schedule_pdf.surgery_type', 'Type')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.vpa }]}>
        {t('pages.hospital.surgery.schedule_pdf.vpa', 'VPA')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.surgeon }]}>
        {t('pages.hospital.surgery.schedule_pdf.surgeon', 'Chirurgien')}
      </Text>
      <Text style={[tableStyles.headerCell, { width: COL.anesthetist }]}>
        {t('pages.hospital.surgery.schedule_pdf.anesthetist', 'Anesthésiste')}
      </Text>
    </View>
  );

  const renderRow = (row: SurgeryPDFRow, index: number) => (
    <View key={index} style={tableStyles.row}>
      <Text style={[tableStyles.cell, { width: COL.num }]}>{index + 1}</Text>
      <Text style={[tableStyles.cell, { width: COL.patient }]}>{row.patientName}</Text>
      <Text style={[tableStyles.cell, { width: COL.healthId }]}>{row.healthId}</Text>
      <Text style={[tableStyles.cell, { width: COL.dob }]}>{row.dob}</Text>
      <Text style={[tableStyles.cell, { width: COL.dateTime }]}>{row.dateTime}</Text>
      <Text style={[tableStyles.cell, { width: COL.indication }]}>{row.indication}</Text>
      <Text style={[tableStyles.cell, { width: COL.surgeryType }]}>{row.surgeryType}</Text>
      <Text style={[tableStyles.cell, { width: COL.vpa }]}>{row.vpaStatus}</Text>
      <Text style={[tableStyles.cell, { width: COL.surgeon }]}>{row.surgeon}</Text>
      <Text style={[tableStyles.cell, { width: COL.anesthetist }]}>{row.anesthetist}</Text>
    </View>
  );

  const renderContent = () => {
    if (totalPatients === 0) {
      return (
        <View style={{ marginTop: 30, alignItems: 'center' }}>
          <Text style={[typographyStyles.h6, { fontWeight: 'bold', color: '#666' }]}>
            {t('pages.hospital.surgery.schedule_pdf.no_surgeries', 'Aucune chirurgie programmée')}
          </Text>
        </View>
      );
    }

    // Group by date for day headers
    const dayMap = new Map<string, GroupedSurgeryData[]>();
    for (const group of weekData) {
      const key = group.date.toISOString().split('T')[0];
      if (!dayMap.has(key)) dayMap.set(key, []);
      dayMap.get(key)!.push(group);
    }

    return (
      <View style={{ marginTop: 8 }}>
        {Array.from(dayMap.entries()).map(([dateKey, groups]) => (
          <View key={dateKey}>
            {/* Day header */}
            <Text style={tableStyles.dayHeader}>
              {formatDayHeaderForPDF(groups[0].date, normalizedLocale)}
            </Text>

            {groups.map((group, gi) => (
              <View key={gi}>
                {/* OR sub-header (always show theater name) */}
                <Text style={tableStyles.agendaHeader}>{group.agendaName}</Text>
                {renderTableHeader()}
                {group.rows.map((row, ri) => renderRow(row, ri))}
              </View>
            ))}
          </View>
        ))}
      </View>
    );
  };

  return (
    <PDFDocument
      title={documentTitle}
      config={config}
      orientation="landscape"
    >
      {renderContent()}
    </PDFDocument>
  );
};

export default SurgerySchedulePDFDocument;
